
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface StructuredDataProps {
  type?: 'organization' | 'service' | 'product' | 'article' | 'breadcrumb';
  data?: any;
}

export const StructuredData: React.FC<StructuredDataProps> = ({ type = 'organization', data }) => {
  const location = useLocation();

  useEffect(() => {
    const baseUrl = window.location.origin;
    
    let structuredData: any = {};

    switch (type) {
      case 'organization':
        structuredData = {
          "@context": "https://schema.org",
          "@type": "HealthcareOrganization",
          "name": "Médicace",
          "description": "Plateforme de santé digitale complète",
          "url": baseUrl,
          "logo": `${baseUrl}/lovable-uploads/8003781b-4d38-497c-8862-00124549a126.png`,
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "availableLanguage": "French"
          },
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "FR"
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": `${baseUrl}/services?q={search_term_string}`,
            "query-input": "required name=search_term_string"
          }
        };
        break;

      case 'service':
        structuredData = {
          "@context": "https://schema.org",
          "@type": "MedicalOrganization",
          "name": data?.name || "Services Médicaux Médicace",
          "description": data?.description || "Services de téléconsultation et soins médicaux",
          "provider": {
            "@type": "Organization",
            "name": "Médicace"
          },
          "areaServed": "France",
          "availableLanguage": "French",
          "offers": {
            "@type": "Offer",
            "description": "Consultations médicales en ligne",
            "category": "Soins de santé"
          }
        };
        break;

      case 'breadcrumb':
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const breadcrumbItems = [
          { name: 'Accueil', url: baseUrl }
        ];
        
        let currentPath = '';
        pathSegments.forEach((segment, index) => {
          currentPath += `/${segment}`;
          const name = segment.charAt(0).toUpperCase() + segment.slice(1).replace('-', ' ');
          breadcrumbItems.push({
            name,
            url: `${baseUrl}${currentPath}`
          });
        });

        structuredData = {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": breadcrumbItems.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": item.url
          }))
        };
        break;

      case 'article':
        structuredData = {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": data?.title,
          "description": data?.description,
          "author": {
            "@type": "Organization",
            "name": "Médicace"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Médicace",
            "logo": {
              "@type": "ImageObject",
              "url": `${baseUrl}/lovable-uploads/8003781b-4d38-497c-8862-00124549a126.png`
            }
          },
          "datePublished": data?.publishedTime,
          "dateModified": data?.modifiedTime || data?.publishedTime,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `${baseUrl}${location.pathname}`
          }
        };
        break;
    }

    // Add the structured data to the page
    const scriptId = `structured-data-${type}`;
    let script = document.getElementById(scriptId) as HTMLScriptElement;
    
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    
    script.textContent = JSON.stringify(structuredData);

    return () => {
      // Cleanup on unmount
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [type, data, location.pathname]);

  return null;
};
